import { createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { entityOfficerInvitationApi } from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import EntityOfficerInvitation from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { EntityOfficerInvitationJSON } from "legal-entities/invitations/officer-invitations/_schema/entity-officer-invitation-schema";

type OfficerInvitationState = {
  entries: Record<string, EntityOfficerInvitationJSON>;
  loading: Record<string, boolean>;
  error: Record<string, SerializedError | null>;
};

const initialState: OfficerInvitationState = {
  entries: {},
  loading: {},
  error: {},
};

const officerInvitationsSlice = createSlice({
  name: "officerInvitations",
  initialState,
  reducers: {
    addEntityOfficerInvitation: (state, action: PayloadAction<EntityOfficerInvitation>) => {
      const invitation = action.payload;
      if (!invitation.id?.value) return;
      state.entries[invitation.id.value] = invitation.toJSON();
    },
    removeEntityOfficerInvitation: (state, action: PayloadAction<EntityOfficerInvitation>) => {
      const invitation = action.payload;
      if (!invitation.id?.value) return;
      delete state.entries[invitation.id.value];
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(entityOfficerInvitationApi.endpoints.getOfficerInvitationById.matchPending, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.id?.value;
      if (!invitationId) return;
      state.loading[invitationId] = true;
      state.error[invitationId] = null;
    });
    builder.addMatcher(entityOfficerInvitationApi.endpoints.getOfficerInvitationById.matchFulfilled, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.id?.value;
      if (!invitationId) return;
      state.entries[invitationId] = action.payload.toJSON();
      state.loading[invitationId] = false;
      state.error[invitationId] = null;
    });
    builder.addMatcher(entityOfficerInvitationApi.endpoints.getOfficerInvitationById.matchRejected, (state, action) => {
      const invitationId = action.meta.arg.originalArgs.id?.value;
      if (!invitationId) return;
      state.loading[invitationId] = false;
      state.error[invitationId] = action.error;
    });

    builder.addMatcher(entityOfficerInvitationApi.endpoints.getOpenOfficerInvitations.matchPending, (state) => {
      state.loading["allOpen"] = true;
      state.error["allOpen"] = null;
    });
    builder.addMatcher(entityOfficerInvitationApi.endpoints.getOpenOfficerInvitations.matchFulfilled, (state, action) => {
      state.loading["allOpen"] = false;
      for (const invitation of action.payload.data) {
        if (!invitation.id?.value) continue;
        state.entries[invitation.id.value] = invitation.toJSON();
      }
    });
    builder.addMatcher(entityOfficerInvitationApi.endpoints.getOpenOfficerInvitations.matchRejected, (state, action) => {
      state.loading["allOpen"] = false;
      state.error["allOpen"] = action.error;
    });
  }
});

export const { addEntityOfficerInvitation, removeEntityOfficerInvitation } =
  officerInvitationsSlice.actions;

export const getEntityOfficerInvitationById = (id: Guid) => 
  (state: RootState) => {
    const json = state.entityOfficerInvitations.entries[id.value];
    return EntityOfficerInvitation.fromJSON(json);
  };
export const getIsLoadingEntityOfficerInvitationById = (id: Guid) =>
  (state: RootState) => state.entityOfficerInvitations.loading[id.value]
export const getErrorLoadingEntityOfficerInvitationById = (id: Guid) =>
  (state: RootState) => state.entityOfficerInvitations.error[id.value]

export const getOpenEntityOfficerInvitations = () =>
  (state: RootState) => {
    const json = Object.values(state.entityOfficerInvitations.entries);
    return EntityOfficerInvitation.fromJSONArray(json);
  };
export const getIsLoadingOpenEntityOfficerInvitations = () =>
  (state: RootState) => state.entityOfficerInvitations.loading;
export const getErrorLoadingOpenEntityOfficerInvitations = () =>
  (state: RootState) => state.entityOfficerInvitations.error;

export default officerInvitationsSlice;
